"use client";

import React from 'react'
import clsx from "clsx";

interface CustomSelectProps {
    value: string;
    placeholder: string;
    options: { label: string; value: string }[];
    variant?: "light" | "dark";
    onChange: (value: string) => void;
    id?: string;
    /** Show the in-dropdown search box. Defaults to true (e.g. Country). */
    searchable?: boolean;
}

// ─── Sleek scrollbar styles (scoped, injected once) ──────────────────────────
const SCROLLBAR_STYLES = `
  .dropdown-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgba(150,150,150,0.4) transparent;
  }
  .dropdown-scroll::-webkit-scrollbar {
    width: 4px;
  }
  .dropdown-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
  .dropdown-scroll::-webkit-scrollbar-thumb {
    background-color: rgba(150,150,150,0.4);
    border-radius: 9999px;
  }
  .dropdown-scroll::-webkit-scrollbar-thumb:hover {
    background-color: rgba(150,150,150,0.7);
  }
`;

const CustomSelect: React.FC<CustomSelectProps> = ({
    value,
    placeholder,
    options,
    variant = "light",
    onChange,
    id,
    searchable = true,
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");
    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const selectedLabel =
        options.find((o) => o.value === value)?.label ?? placeholder;

    const isDark = variant === "dark";

    const filteredOptions = searchable
        ? options.filter((opt) =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : options;

    return (
        <div ref={ref} className="relative w-full" id={id}>
            <style>{SCROLLBAR_STYLES}</style>

            {/* Trigger */}
            <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                onClick={() => {
                    setIsOpen((prev) => !prev);

                    if (!isOpen) {
                        setSearchTerm("");
                    }
                }}
                className={clsx(
                    "w-full flex items-center justify-between border-b border-solid bg-transparent outline-none min-h-[59px] text-base transition-colors",
                    isDark
                        ? "border-white/20 text-white"
                        : "border-b-[color:var(--Stroke-2,#E3E3E3)] text-zinc-600",
                    !value && (isDark ? "text-white/60" : "text-zinc-500")
                )}
            >
                <span>{selectedLabel}</span>

                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={clsx(
                        "shrink-0 transition-transform",
                        isOpen && "rotate-180",
                        isDark ? "text-white" : "text-zinc-500"
                    )}
                    aria-hidden
                >
                    <path
                        d="M4 6l4 4 4-4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div
                    className={clsx(
                        "absolute top-full left-0 z-20 w-full mt-1 rounded-md overflow-hidden",
                        isDark
                            ? "bg-[#1a1f2e] border border-white/20"
                            : "bg-white border border-gray-100 shadow-lg"
                    )}
                >
                    {/* Search */}
                    {searchable && (
                        <div className="p-2 border-b border-gray-200/20">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                className={clsx(
                                    "w-full px-3 py-2 text-sm rounded-md outline-none",
                                    isDark
                                        ? "bg-white/10 text-white placeholder:text-white/60"
                                        : "bg-gray-50 text-zinc-700 placeholder:text-zinc-500"
                                )}
                            />
                        </div>
                    )}

                    {/* Options */}
                    <ul
                        role="listbox"
                        className="dropdown-scroll max-h-52 overflow-y-auto"
                    >
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((opt) => (
                                <li
                                    key={opt.value}
                                    role="option"
                                    aria-selected={opt.value === value}
                                    onClick={() => {
                                        onChange(opt.value);
                                        setIsOpen(false);
                                        setSearchTerm("");
                                    }}
                                    className={clsx(
                                        "px-4 py-2 cursor-pointer text-sm transition-colors",
                                        isDark ? "hover:bg-white/10" : "hover:bg-gray-100",
                                        opt.value === value &&
                                        (isDark
                                            ? "bg-white/15 font-medium"
                                            : "bg-gray-50 font-medium")
                                    )}
                                >
                                    {opt.label}
                                </li>
                            ))
                        ) : (
                            <li
                                className={clsx(
                                    "px-4 py-3 text-sm",
                                    isDark ? "text-white/60" : "text-zinc-500"
                                )}
                            >
                                No results found
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default CustomSelect

